import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Package, Clock, Hammer, CheckCircle2, BellRing, AlertTriangle,
  ArrowRight, Plus, Users, Scale, TrendingUp, Award,
} from "lucide-react";

function ClickableKpi({ label, value, icon: Icon, accent, href, testid }) {
  return (
    <Link to={href} className="block group" data-testid={testid}>
      <Card className="p-6 border-border group-hover:-translate-y-0.5 group-hover:shadow-sm group-hover:border-zinc-900 transition-all cursor-pointer h-full">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-zinc-500">{label}</div>
            <div className="font-heading font-black text-4xl tracking-tighter mt-2 num">{value}</div>
          </div>
          <div className={`w-9 h-9 rounded-md grid place-items-center ${accent}`}>
            <Icon className="w-4 h-4" strokeWidth={1.7} />
          </div>
        </div>
        <div className="text-xs text-zinc-400 group-hover:text-zinc-900 mt-3 inline-flex items-center gap-1 transition-colors">
          View list <ArrowRight className="w-3 h-3" />
        </div>
      </Card>
    </Link>
  );
}

function WeightCard({ label, grams, accent, testid }) {
  return (
    <Card className="p-6 border-border" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-zinc-500">{label}</div>
          <div className="font-heading font-black text-3xl tracking-tighter mt-2 num">
            {(grams || 0).toFixed(grams >= 100 ? 0 : 2)}
            <span className="text-base text-zinc-500 ml-1 font-medium">g</span>
          </div>
        </div>
        <div className={`w-9 h-9 rounded-md grid place-items-center ${accent}`}>
          <Scale className="w-4 h-4" strokeWidth={1.7} />
        </div>
      </div>
    </Card>
  );
}

function fmtG(n) {
  return `${(n || 0).toFixed(n >= 100 ? 0 : 2)} g`;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState("year");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  useEffect(() => {
    const params = { range };
    if (range === "custom") {
      if (customFrom) params.date_from = new Date(customFrom).toISOString();
      if (customTo) {
        const end = new Date(customTo);
        end.setHours(23, 59, 59, 999);
        params.date_to = end.toISOString();
      }
    }
    api.get("/dashboard/analytics", { params }).then((r) => setAnalytics(r.data));
  }, [range, customFrom, customTo]);

  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const k = data.kpis;
  const maxCat = analytics
    ? Math.max(1, ...analytics.category_breakdown.map((c) => c.count))
    : 1;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500">Overview</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Dashboard</h1>
          <p className="text-zinc-500 mt-2 text-sm">A live snapshot of your wholesale custom-order workflow.</p>
        </div>
        <Link to="/orders/new">
          <Button className="bg-zinc-900 hover:bg-zinc-800 h-10" data-testid="dashboard-new-order">
            <Plus className="w-4 h-4 mr-1.5" /> New Order
          </Button>
        </Link>
      </div>

      {/* KPIs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl tracking-tight">Orders Completed & In-flight</h2>
          <span className="text-xs text-zinc-400">click any card to filter the orders list</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <ClickableKpi label="Total Orders" value={k.total_orders} icon={Package} accent="bg-zinc-900 text-white" href="/orders" testid="kpi-total-orders" />
          <ClickableKpi label="Pending" value={k.pending} icon={Clock} accent="bg-yellow-100 text-yellow-800" href="/orders?status=Pending" testid="kpi-pending" />
          <ClickableKpi label="In Progress" value={k.in_progress} icon={Hammer} accent="bg-violet-100 text-violet-800" href="/orders?status=In%20Progress" testid="kpi-in-progress" />
          <ClickableKpi label="Ready" value={k.ready} icon={CheckCircle2} accent="bg-emerald-100 text-emerald-800" href="/orders?status=Ready" testid="kpi-ready" />
          <ClickableKpi label="Delivered" value={k.delivered} icon={CheckCircle2} accent="bg-zinc-900 text-white" href="/orders?status=Delivered" testid="kpi-delivered" />
        </div>
      </div>

      {/* Weight analytics */}
      <div>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h2 className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Weight & Performance
            </h2>
            <p className="text-xs text-zinc-500 mt-1">Production analytics by jewellery weight & category</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-44" data-testid="analytics-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            {range === "custom" && (
              <>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-40" data-testid="analytics-date-from" />
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-40" data-testid="analytics-date-to" />
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <WeightCard label="Weight Completed" grams={analytics?.weight_completed} accent="bg-emerald-100 text-emerald-800" testid="kpi-weight-completed" />
          <WeightCard label="Weight In Progress" grams={analytics?.weight_in_progress} accent="bg-violet-100 text-violet-800" testid="kpi-weight-in-progress" />
          <Card className="p-6 border-border" data-testid="kpi-orders-in-range">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline text-zinc-500">Orders Completed</div>
                <div className="font-heading font-black text-3xl tracking-tighter mt-2 num">
                  {analytics?.orders_completed || 0}
                  <span className="text-base text-zinc-500 ml-1 font-medium">/ {analytics?.total_orders || 0}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">in selected range</div>
              </div>
              <div className="w-9 h-9 rounded-md grid place-items-center bg-zinc-900 text-white">
                <Award className="w-4 h-4" strokeWidth={1.7} />
              </div>
            </div>
          </Card>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-4">
          {/* Category breakdown */}
          <Card className="p-6">
            <h3 className="font-heading font-bold text-lg tracking-tight mb-4">Category Breakdown</h3>
            {!analytics || analytics.category_breakdown.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">No data in this range yet.</div>
            ) : (
              <ul className="space-y-3" data-testid="category-breakdown">
                {analytics.category_breakdown.map((c) => (
                  <li key={c.category}>
                    <Link
                      to={`/orders?jewellery_type=${encodeURIComponent(c.category)}`}
                      className="block group"
                    >
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium group-hover:underline">{c.category}</span>
                        <span className="text-zinc-500 num">
                          {c.count} order(s){c.weight_completed > 0 && <> · <span className="text-zinc-700 font-medium">{fmtG(c.weight_completed)}</span> done</>}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-zinc-900 rounded-full transition-all"
                          style={{ width: `${(c.count / maxCat) * 100}%` }}
                        />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Karigar performance */}
          <Card className="p-6">
            <h3 className="font-heading font-bold text-lg tracking-tight mb-4 flex items-center gap-2">
              <Hammer className="w-4 h-4" /> Karigar Performance
            </h3>
            {!analytics || analytics.karigar_performance.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">
                No deliveries in this range yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]" data-testid="karigar-performance-table">
                <thead>
                  <tr className="text-left text-zinc-500">
                    <th className="overline pb-2 font-semibold">Karigar</th>
                    <th className="overline pb-2 font-semibold text-right">Orders</th>
                    <th className="overline pb-2 font-semibold text-right">Weight</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {analytics.karigar_performance.map((p) => (
                    <tr key={p.karigar_id}>
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2 text-right num">{p.orders_completed}</td>
                      <td className="py-2 text-right num">{fmtG(p.weight_completed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Recent + reminders + workload */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-xl tracking-tight">Recent Orders</h3>
            <Link to="/orders" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {data.recent_orders.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No orders yet.</div>
          ) : (
            <div className="divide-y divide-border" data-testid="recent-orders-list">
              {data.recent_orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/orders/${o.id}`}
                  className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded-md"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-zinc-500">{o.order_no}</span>
                      <StatusBadge status={o.status} />
                      <PriorityBadge priority={o.priority} />
                    </div>
                    <div className="font-medium text-sm mt-1 truncate">{o.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{o.customer_name || "—"}</div>
                  </div>
                  <div className="text-right text-xs text-zinc-500">
                    {o.expected_delivery
                      ? <>Due {new Date(o.expected_delivery).toLocaleDateString()}</>
                      : new Date(o.created_at).toLocaleDateString()}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
                <BellRing className="w-4 h-4 text-gold-500" /> Reminders
              </h3>
              <Link to="/reminders" className="text-xs text-zinc-500 hover:text-zinc-900">All</Link>
            </div>
            {data.upcoming_reminders.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">Nothing scheduled.</div>
            ) : (
              <ul className="space-y-3" data-testid="upcoming-reminders">
                {data.upcoming_reminders.map((r) => (
                  <li key={r.id} className="text-sm">
                    <Link to={`/orders/${r.order_id}`} className="block hover:bg-zinc-50 -mx-2 px-2 py-1 rounded-md">
                      <div className="font-medium truncate">{r.title}</div>
                      <div className="text-xs text-zinc-500">{r.order_no} · {new Date(r.due_at).toLocaleString()}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="font-heading font-bold text-xl tracking-tight flex items-center gap-2 mb-4">
              <Hammer className="w-4 h-4" /> Karigar Workload
            </h3>
            {data.karigar_workload.length === 0 ? (
              <div className="text-sm text-zinc-500">Add karigars to see workload.</div>
            ) : (
              <ul className="space-y-2.5">
                {data.karigar_workload.slice(0, 5).map((k) => (
                  <li key={k.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{k.name}</span>
                    <span className="num font-semibold">{k.active}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-heading font-bold text-xl tracking-tight flex items-center gap-2 mb-4">
            <Users className="w-4 h-4" /> Top Customers
          </h3>
          {data.top_customers.length === 0 ? (
            <div className="text-sm text-zinc-500">No data yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {data.top_customers.map((c) => (
                <li key={c.customer_id} className="py-2.5 flex items-center justify-between">
                  <Link to={`/customers/${c.customer_id}`} className="text-sm hover:underline">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.shop_name || "—"}</div>
                  </Link>
                  <span className="num text-sm font-semibold">{c.count} orders</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-heading font-bold text-xl tracking-tight flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-600" /> Delayed Orders
          </h3>
          {data.delayed_orders.length === 0 ? (
            <div className="text-sm text-zinc-500">No delayed orders.</div>
          ) : (
            <ul className="divide-y divide-border" data-testid="delayed-orders">
              {data.delayed_orders.slice(0, 5).map((o) => (
                <li key={o.id} className="py-2.5">
                  <Link to={`/orders/${o.id}`} className="text-sm hover:bg-zinc-50 -mx-2 px-2 py-1 rounded-md block">
                    <div className="flex justify-between">
                      <span className="font-medium truncate">{o.title}</span>
                      <StatusBadge status={o.status} />
                    </div>
                    <div className="text-xs text-zinc-500">{o.order_no} · expected {new Date(o.expected_delivery).toLocaleDateString()}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
